import React, { useState, useEffect, useCallback } from 'react';
import {
    History, ChevronLeft, ChevronRight, CheckCircle2, AlertCircle,
    Clock, List, Activity, Euro, CalendarCheck, X, Trash2, Infinity,
    ArrowRight, FileText, ExternalLink, Edit2, Check
} from 'lucide-react';
import './PaymentHistorySocio.css';
import API_BASE_URL from '../../config/api';

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1984;

const PaymentHistorySocio = ({ 
    isOpen, 
    onClose, 
    userId, 
    overdueMonths = [], 
    isAdmin = false, 
    onPaymentSuccess = null, 
    paymentStatus = 'Unpaid', 
    quotaAmount = 0, 
    paymentPreference = 'Monthly',
    selectedMonth = null,
    selectedYear = null
}) => {
    const [year, setYear] = useState(() => selectedYear || CURRENT_YEAR);
    const [payments, setPayments] = useState([]);
    const [inscriptions, setInscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [editingInscId, setEditingInscId] = useState(null);
    const [editInscValue, setEditInscValue] = useState('');
    const [quotaPaidUntil, setQuotaPaidUntil] = useState(null);
    const [editingQuotaPaid, setEditingQuotaPaid] = useState(false);
    const [tempQuotaPaid, setTempQuotaPaid] = useState('');

    useEffect(() => {
        if (selectedYear) setYear(selectedYear);
    }, [selectedYear]);

    const fetchHistory = useCallback(async (y) => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const m = (y === (selectedYear || CURRENT_YEAR) && selectedMonth) ? selectedMonth : (y < CURRENT_YEAR ? 12 : (new Date().getMonth() + 1));
            const res = await fetch(
                `${apiUrl}/payment/history?userId=${userId}&year=${y}&month=${m}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error('Erro ao carregar histórico');
            const data = await res.json();
            setPayments(data.payments || []);
            setInscriptions(data.inscriptions || []);
            setQuotaPaidUntil(data.quotaPaidUntil || null);
        } catch (e) {
            setError(e.message);
            setPayments([]);
            setInscriptions([]);
            setQuotaPaidUntil(null);
        } finally {
            setLoading(false);
        }
    }, [userId, selectedMonth, selectedYear]);

    const handlePayInscription = async (athleteTeamId, sportName) => {
        if (!window.confirm(`Deseja marcar a inscrição de "${sportName}" como paga manualmente?`)) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const res = await fetch(`${apiUrl}/payment/inscription/mark-paid`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ athleteTeamId })
            });

            if (!res.ok) throw new Error('Erro ao validar inscrição');

            fetchHistory(year);
            if (onPaymentSuccess) onPaymentSuccess();

        } catch (err) {
            console.error('Error paying inscription:', err);
            alert('Erro ao validar inscrição: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveInscriptionFee = async (item) => {
        const parsed = parseFloat(editInscValue);
        if (isNaN(parsed) || parsed < 0) {
            alert('Por favor insira um valor válido para a inscrição (ex: 25.00)');
            return;
        }

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const res = await fetch(`${apiUrl}/payment/admin/custom-inscription-fee`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    athleteTeamId: item.athleteTeamId,
                    customInscriptionFee: parsed
                })
            });

            if (!res.ok) throw new Error('Erro ao atualizar valor da inscrição');

            setEditingInscId(null);
            setEditInscValue('');
            fetchHistory(year);
            if (onPaymentSuccess) onPaymentSuccess();
        } catch (err) {
            console.error('Error saving inscription fee:', err);
            alert('Erro ao atualizar valor da inscrição: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveQuotaPaidUntil = async (val) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const dateVal = val ? `${val}-01T00:00:00Z` : null;
            const res = await fetch(`${apiUrl}/payment/admin/quota-paid-until`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId,
                    quotaPaidUntil: dateVal
                })
            });

            if (!res.ok) throw new Error('Erro ao atualizar quota paga até');
            const resData = await res.json();
            setQuotaPaidUntil(resData.quotaPaidUntil || null);
            setEditingQuotaPaid(false);
            fetchHistory(year);
            if (onPaymentSuccess) onPaymentSuccess();
        } catch (err) {
            console.error('Error saving quota paid until:', err);
            alert('Erro ao atualizar quota paga até: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleEmitInvoiceForPayment = async (rawPayment, label) => {
        const hasExistingInvoice = !!rawPayment?.moloniDocumentId || !!rawPayment?.hasInvoice;

        if (!hasExistingInvoice) {
            if (!window.confirm(`Deseja emitir uma Fatura-Recibo no Moloni para o registo "${label}"?`)) return;
        }

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const res = await fetch(`${apiUrl}/payment/admin/generate-invoice`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId,
                    paymentIds: [rawPayment.id]
                })
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Erro ao processar fatura');

            const pdfUrl = data.invoice?.pdfUrl || data.pdfUrl;
            if (pdfUrl) {
                window.open(pdfUrl, '_blank');
            } else {
                alert(data.message || 'Fatura processada com sucesso no Moloni!');
            }

            fetchHistory(year);
        } catch (err) {
            console.error('Error generating invoice:', err);
            alert('Erro ao processar fatura: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeletePayment = async (id, label) => {
        if (!window.confirm(`Tem a certeza que deseja eliminar o registo de "${label}"?`)) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const res = await fetch(`${apiUrl}/payment/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Erro ao eliminar pagamento');

            fetchHistory(year);
            if (onPaymentSuccess) onPaymentSuccess();

        } catch (err) {
            console.error('Error deleting payment:', err);
            alert('Erro ao eliminar pagamento: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteInscription = async (athleteTeamId, label) => {
        if (!window.confirm(`Tem a certeza que deseja anular e eliminar a inscrição de "${label}"?`)) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${API_BASE_URL}/api`;
            const res = await fetch(`${apiUrl}/payment/inscription/mark-unpaid`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ athleteTeamId })
            });

            if (!res.ok) throw new Error('Erro ao anular inscrição');

            fetchHistory(year);
            if (onPaymentSuccess) onPaymentSuccess();

        } catch (err) {
            console.error('Error deleting inscription:', err);
            alert('Erro ao anular inscrição: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };


    useEffect(() => {
        if (isOpen) fetchHistory(year);
    }, [year, isOpen, fetchHistory]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    const remainingInscriptions = inscriptions.filter(
        ins => !payments.some(p => p.description?.toLowerCase().includes('inscrição') && p.description?.toLowerCase().includes(ins.sportName?.toLowerCase()))
    );

    const totalPayments = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const totalInscriptions = remainingInscriptions.filter(i => i.paid).reduce((s, i) => s + (i.amount || 0), 0);
    const totalPaid = totalPayments + totalInscriptions;

    const isLifetime = payments.some(p => p.periodYear >= 2099);
    const overdueThisYear = overdueMonths.filter(m => m.periodYear === year);
    const canGoBack = year > MIN_YEAR;
    const canGoForward = year < CURRENT_YEAR + 5;

    const labelForPayment = (p) => {
        if (p.periodYear >= 2099) return 'Quota Vitalícia';
        if (p.description?.toLowerCase().includes('inscrição')) {
            const pYear = p.periodYear || (p.paymentDate ? new Date(p.paymentDate).getFullYear() : year);
            const baseDesc = p.description;
            if (!baseDesc.includes('/')) {
                return `${baseDesc} (Época ${pYear}/${pYear + 1})`;
            }
            return baseDesc;
        }
        if (!p.periodMonth || p.description?.includes("Anual") || p.label?.includes("Anual")) {
            if (p.periodMonth && p.periodYear) {
                try {
                    const startMonth = p.periodMonth;
                    const startYear = p.periodYear;
                    const endMonth = startMonth === 1 ? 12 : startMonth - 1;
                    const endYear = startMonth === 1 ? startYear : startYear + 1;
                    return `Quota Anual (${MONTHS_PT[startMonth - 1]} ${startYear} - ${MONTHS_PT[endMonth - 1]} ${endYear})`;
                } catch (e) {
                    // fallback below
                }
            }
            const payDate = p.paymentDate || p.createdAt;
            if (payDate) {
                try {
                    const start = new Date(payDate);
                    const end = new Date(start);
                    end.setFullYear(start.getFullYear() + 1);

                    const formatStr = (d) => `${MONTHS_PT[d.getMonth()]} ${d.getFullYear()}`;
                    return `Quota Anual (${formatStr(start)} - ${formatStr(end)})`;
                } catch (e) {
                    // fallback below
                }
            }
            return `Quota Anual ${p.periodYear}`;
        }
        return `Quota ${MONTHS_PT[p.periodMonth - 1]} ${p.periodYear}`;
    };

    const methodLabel = (m) => {
        if (!m || m === 'Histórico') return 'Histórico';
        if (m === 'MB') return 'Multibanco';
        if (m === 'Manual') return 'Manual';
        return m;
    };

    // ── Build Unified & Chronologically Sorted History List ─────────────
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const isAnnual = paymentPreference === 'Annual';

    const unifiedList = [];

    // 1. Paid Payments
    payments.forEach(p => {
        const isInscription = p.description?.toLowerCase().includes('inscrição');
        unifiedList.push({
            id: `pay-${p.id}`,
            itemType: isInscription ? 'inscription' : 'quota',
            displayStatus: 'Completed',
            title: labelForPayment(p),
            amount: p.amount || 0,
            paymentDate: p.paymentDate || p.createdAt,
            paymentMethod: p.paymentMethod,
            periodMonth: p.periodMonth ?? (isInscription ? 9 : null),
            periodYear: p.periodYear ?? year,
            isImported: p.paymentMethod === 'Histórico',
            isVitalicio: (p.periodYear ?? 0) >= 2099,
            isInscription: isInscription,
            rawPayment: p
        });
    });

    // 2. Unpaid / Standalone Inscriptions (only if not already represented in payments)
    remainingInscriptions.forEach((ins, idx) => {
        const seasonLabel = ins.season ? ` (Época ${ins.season})` : ` (Época ${year}/${year + 1})`;
        unifiedList.push({
            id: `ins-${idx}`,
            itemType: 'inscription',
            displayStatus: ins.paid ? 'Completed' : 'Unpaid',
            title: `Inscrição: ${ins.sportName}${seasonLabel}`,
            amount: ins.amount || 0,
            paymentDate: ins.paymentDate,
            periodMonth: 9,
            periodYear: year,
            paid: ins.paid,
            athleteTeamId: ins.athleteTeamId,
            sportName: ins.sportName,
            isInscription: true
        });
    });

    // 3. Current Month or Annual Quota (if in current year and not already paid)
    if (year === currentYear) {
        if (isAnnual) {
            const hasAnnualPaid = payments.some(p =>
                p.periodYear === year &&
                (p.periodMonth === null || p.periodMonth === 0 || p.description?.includes("Anual") || p.label?.includes("Anual"))
            );
            if (!hasAnnualPaid) {
                const isPendente = paymentStatus === 'Pendente';
                unifiedList.push({
                    id: 'current-annual',
                    itemType: 'quota',
                    displayStatus: isPendente ? 'Pending' : 'Unpaid',
                    title: `Quota Anual ${year}`,
                    amount: quotaAmount || 0,
                    periodMonth: null,
                    periodYear: year,
                    isCurrent: true
                });
            }
        } else {
            const isCurrentPaid = payments.some(p => p.periodYear === currentYear && p.periodMonth === currentMonth && !p.description?.toLowerCase().includes('inscrição'));
            if (!isCurrentPaid) {
                const isPendente = paymentStatus === 'Pendente';
                unifiedList.push({
                    id: 'current-month',
                    itemType: 'quota',
                    displayStatus: isPendente ? 'Pending' : 'Unpaid',
                    title: `${MONTHS_PT[currentMonth - 1]} ${currentYear}`,
                    amount: quotaAmount || 0,
                    periodMonth: currentMonth,
                    periodYear: currentYear,
                    isCurrent: true
                });
            }
        }
    }

    // 4. Overdue Quotas (Monthly only, excluding current month if already added)
    if (!isAnnual) {
        overdueThisYear
            .filter(m => !(m.periodYear === currentYear && m.periodMonth === currentMonth))
            .filter(m => !payments.some(p => p.periodYear === m.periodYear && p.periodMonth === m.periodMonth && !p.description?.toLowerCase().includes('inscrição')))
            .forEach((m, idx) => {
                unifiedList.push({
                    id: `overdue-${idx}`,
                    itemType: 'quota',
                    displayStatus: 'Overdue',
                    title: `${MONTHS_PT[m.periodMonth - 1]} ${m.periodYear}`,
                    amount: m.amount || 0,
                    periodMonth: m.periodMonth,
                    periodYear: m.periodYear,
                    isOverdue: true
                });
            });
    }

    // ── Sort: Most recent on top ─────────────────────────────────────────
    unifiedList.sort((a, b) => {
        // 1. Year descending
        const yearA = a.periodYear ?? (a.paymentDate ? new Date(a.paymentDate).getFullYear() : 0);
        const yearB = b.periodYear ?? (b.paymentDate ? new Date(b.paymentDate).getFullYear() : 0);
        if (yearB !== yearA) return yearB - yearA;

        // 2. Month descending (Inscriptions for season placed alongside September at 9.5)
        const monthA = a.isInscription ? 9.5 : (a.periodMonth ?? (isAnnual ? 12 : 0));
        const monthB = b.isInscription ? 9.5 : (b.periodMonth ?? (isAnnual ? 12 : 0));
        if (monthB !== monthA) return monthB - monthA;

        // 3. PaymentDate descending
        const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
        const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
        if (dateB !== dateA) return dateB - dateA;

        // 4. Completed payments before unpaid/pending for same period
        if (a.displayStatus === 'Completed' && b.displayStatus !== 'Completed') return -1;
        if (b.displayStatus === 'Completed' && a.displayStatus !== 'Completed') return 1;

        return 0;
    });

    const isEmpty = unifiedList.length === 0;

    return (
        <div className="phs-overlay" onClick={onClose}>
            <div className="phs-modal" onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="phs-modal-header">
                    <div className="phs-title">
                        <div className="phs-title-icon-wrapper">
                            <History size={20} />
                        </div>
                        <div className="phs-title-text">
                            <h2>Ficha do Sócio</h2>
                            <p>Histórico de pagamentos e quotas</p>
                        </div>
                    </div>
                    <div className="phs-header-actions">
                        <div className="phs-year-nav">
                            <button
                                className="phs-nav-btn"
                                onClick={() => setYear(y => y - 1)}
                                disabled={!canGoBack}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <span className="phs-year-label">{year}</span>
                            <button
                                className="phs-nav-btn"
                                onClick={() => setYear(y => y + 1)}
                                disabled={!canGoForward}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                        <button className="phs-close-btn" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* ── Quota de Sócio Paga Até Bar ── */}
                <div className="phs-quota-bar">
                    <div className="phs-quota-bar-main">
                        <div className="phs-quota-bar-label-group">
                            <span className="phs-quota-bar-title">Quota de Sócio Paga Até:</span>
                            {!editingQuotaPaid ? (
                                <span className={`phs-quota-badge ${quotaPaidUntil ? 'is-set' : 'not-set'}`}>
                                    {quotaPaidUntil ? (
                                        new Date(quotaPaidUntil).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })
                                    ) : (
                                        'Não definida (mês a mês)'
                                    )}
                                </span>
                            ) : (
                                <div className="phs-quota-edit-inline">
                                    <input
                                        type="month"
                                        className="phs-month-input"
                                        value={tempQuotaPaid}
                                        onChange={(e) => setTempQuotaPaid(e.target.value)}
                                        disabled={actionLoading}
                                        autoFocus
                                    />
                                    <button
                                        className="phs-btn-save-quota"
                                        title="Guardar"
                                        disabled={actionLoading}
                                        onClick={() => handleSaveQuotaPaidUntil(tempQuotaPaid)}
                                    >
                                        <Check size={14} /> Guardar
                                    </button>
                                    {quotaPaidUntil && (
                                        <button
                                            className="phs-btn-clear-quota"
                                            title="Limpar data limite"
                                            disabled={actionLoading}
                                            onClick={() => {
                                                setTempQuotaPaid('');
                                                handleSaveQuotaPaidUntil('');
                                            }}
                                        >
                                            Limpar
                                        </button>
                                    )}
                                    <button
                                        className="phs-btn-cancel-quota"
                                        title="Cancelar"
                                        disabled={actionLoading}
                                        onClick={() => setEditingQuotaPaid(false)}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {isAdmin && !editingQuotaPaid && (
                            <button
                                className="phs-btn-edit-quota"
                                title="Editar mês até ao qual a quota de sócio está paga"
                                onClick={() => {
                                    setTempQuotaPaid(quotaPaidUntil ? quotaPaidUntil.slice(0, 7) : '');
                                    setEditingQuotaPaid(true);
                                }}
                            >
                                <Edit2 size={13} />
                                <span>{quotaPaidUntil ? 'Alterar' : 'Definir'}</span>
                            </button>
                        )}
                    </div>
                    {quotaPaidUntil && (
                        <div className="phs-quota-bar-hint">
                            * Mensalidades até <strong>{new Date(quotaPaidUntil).toLocaleDateString('pt-PT', { month: '2-digit', year: 'numeric' })}</strong> (inclusive) cobram apenas a mensalidade da modalidade sem valor de quota de sócio.
                        </div>
                    )}
                </div>

                {/* ── Summary Cards ── */}
                {!loading && !isEmpty && (
                    <div className="phs-summary-grid">
                        <div className="phs-summary-card">
                            <div className="phs-card-icon phs-bg-blue">
                                <Euro size={18} />
                            </div>
                            <div className="phs-card-content">
                                <span className="phs-card-label">Total Pago ({year})</span>
                                <strong className="phs-card-value">
                                    {isLifetime ? '—' : `${totalPaid.toFixed(2)}€`}
                                </strong>
                            </div>
                        </div>
                        <div className="phs-summary-card">
                            <div className="phs-card-icon phs-bg-slate">
                                <List size={18} />
                            </div>
                            <div className="phs-card-content">
                                <span className="phs-card-label">Registos</span>
                                <strong className="phs-card-value">{payments.length + remainingInscriptions.filter(i => i.paid).length}</strong>
                            </div>
                        </div>
                        {overdueThisYear.length > 0 && (
                            <div className="phs-summary-card phs-card-warn">
                                <div className="phs-card-icon phs-bg-red">
                                    <AlertCircle size={18} />
                                </div>
                                <div className="phs-card-content">
                                    <span className="phs-card-label">Em atraso</span>
                                    <strong className="phs-card-value phs-text-red">
                                        {overdueThisYear.length} {overdueThisYear.length === 1 ? 'mês' : 'meses'}
                                    </strong>
                                </div>
                            </div>
                        )}
                        {isLifetime && (
                            <div className="phs-summary-card phs-card-gold">
                                <div className="phs-card-icon phs-bg-gold">
                                    <Infinity size={18} />
                                </div>
                                <div className="phs-card-content">
                                    <span className="phs-card-label">Estatuto</span>
                                    <strong className="phs-card-value phs-text-gold">Vitalício</strong>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Body ── */}
                <div className="phs-body">
                    {loading ? (
                        <div className="phs-state">
                            <div className="phs-spinner" />
                            <p>A carregar registos...</p>
                        </div>
                    ) : error ? (
                        <div className="phs-state phs-state--error">
                            <AlertCircle size={40} />
                            <p>{error}</p>
                        </div>
                    ) : isEmpty ? (
                        <div className="phs-state phs-state--empty">
                            <CalendarCheck size={48} className="phs-empty-icon" />
                            <p>Sem registos de pagamentos em <strong>{year}</strong>.</p>
                            {canGoBack && (
                                <button className="phs-btn-switch-year" onClick={() => setYear(y => y - 1)}>
                                    Ver histórico de {year - 1} <ArrowRight size={14} />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="phs-list">
                            {unifiedList.map((item) => {
                                const isCompleted = item.displayStatus === 'Completed';
                                const isPending = item.displayStatus === 'Pending';
                                const isVitalicioItem = item.isVitalicio;
                                const isImportedItem = item.isImported;
                                const isInscriptionItem = item.isInscription;

                                return (
                                    <div
                                        key={item.id}
                                        className={`phs-item ${isCompleted ? 'p-paid' : isPending ? 'p-pending' : 'p-overdue'} ${isImportedItem ? 'item-historic' : ''} ${isVitalicioItem ? 'item-lifetime' : ''} ${isInscriptionItem ? 'phs-item--inscription' : ''}`}
                                    >
                                        <div className="phs-item-left">
                                            <div className={`phs-item-icon ${isVitalicioItem ? 'icon-gold' : isCompleted ? 'icon-green' : isPending ? 'icon-orange' : 'icon-red'}`}>
                                                {isVitalicioItem ? (
                                                    <Infinity size={16} />
                                                ) : isCompleted ? (
                                                    <CheckCircle2 size={16} />
                                                ) : isPending ? (
                                                    <Clock size={16} />
                                                ) : (
                                                    <AlertCircle size={16} />
                                                )}
                                            </div>
                                            <div className="phs-item-info">
                                                <span className="phs-item-title">{item.title}</span>
                                                <div className="phs-item-meta">
                                                    {isCompleted ? (
                                                        <>
                                                            {isImportedItem ? (
                                                                <span className="phs-meta-badge badge-historic">Migrado</span>
                                                            ) : item.paymentDate ? (
                                                                <span className="phs-meta-date">
                                                                    <div className="dot" />
                                                                    {new Date(item.paymentDate).toLocaleDateString('pt-PT', {
                                                                        day: '2-digit', month: 'short', year: 'numeric'
                                                                    })}
                                                                </span>
                                                            ) : null}
                                                            {isInscriptionItem && (
                                                                <span className="phs-meta-badge" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#2563eb', fontWeight: 600 }}>Inscrição</span>
                                                            )}
                                                            {item.paymentMethod && (
                                                                <span className="phs-meta-method">{methodLabel(item.paymentMethod)}</span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="phs-status-pill-row">
                                                            <span className={`phs-status-pill ${isPending ? 'pill-pending' : 'pill-overdue'}`}>
                                                                {isPending ? 'Pagamento Pendente' : item.isCurrent ? 'Mês atual em falta' : isInscriptionItem ? 'Inscrição em falta' : 'Quota em atraso'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* If unpaid inscription and admin, offer action button */}
                                                {isInscriptionItem && !item.paid && item.athleteTeamId && isAdmin && (
                                                    <button
                                                        className="phs-pay-action-btn"
                                                        onClick={() => handlePayInscription(item.athleteTeamId, item.sportName)}
                                                        disabled={actionLoading}
                                                    >
                                                        <Euro size={12} /> Validar Pagamento
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="phs-item-right">
                                            {editingInscId === item.id ? (
                                                <div className="phs-amount-edit-box">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="phs-amount-input"
                                                        value={editInscValue}
                                                        onChange={(e) => setEditInscValue(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <span className="phs-amount-currency">€</span>
                                                    <button
                                                        type="button"
                                                        className="phs-amount-save-btn"
                                                        onClick={() => handleSaveInscriptionFee(item)}
                                                        disabled={actionLoading}
                                                        title="Guardar valor"
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="phs-amount-cancel-btn"
                                                        onClick={() => setEditingInscId(null)}
                                                        title="Cancelar"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="phs-amount-display-box">
                                                    <span className={`phs-item-amount ${isVitalicioItem ? 'text-gold' : isCompleted ? '' : isPending ? 'text-orange' : 'text-red'}`}>
                                                        {isVitalicioItem ? '—' : item.amount > 0 ? `${item.amount.toFixed(2)}€` : (isInscriptionItem ? '0.00€' : '—')}
                                                    </span>
                                                    {isInscriptionItem && !item.paid && isAdmin && (
                                                        <button
                                                            type="button"
                                                            className="phs-amount-edit-btn"
                                                            onClick={() => {
                                                                setEditingInscId(item.id);
                                                                setEditInscValue(item.amount != null ? item.amount.toString() : '');
                                                            }}
                                                            title="Editar valor da inscrição"
                                                        >
                                                            <Edit2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <div className="phs-item-actions">
                                                <span className={`phs-status-pill ${isCompleted ? 'pill-paid' : isPending ? 'pill-pending' : 'pill-overdue'}`}>
                                                    {isCompleted ? 'Pago' : isPending ? 'Pendente' : 'Em falta'}
                                                </span>
                                                {isCompleted && isAdmin && !actionLoading && item.rawPayment && (
                                                    <button
                                                        className={`phs-invoice-btn ${item.rawPayment.moloniDocumentId || item.rawPayment.hasInvoice ? 'phs-invoice-btn--existing' : ''}`}
                                                        onClick={() => handleEmitInvoiceForPayment(item.rawPayment, item.title)}
                                                        title={item.rawPayment.moloniDocumentId || item.rawPayment.hasInvoice ? "Ver Fatura Moloni emitida" : "Emitir / Obter Fatura Moloni"}
                                                    >
                                                        {item.rawPayment.moloniDocumentId || item.rawPayment.hasInvoice ? (
                                                            <ExternalLink size={13} />
                                                        ) : (
                                                            <FileText size={13} />
                                                        )}
                                                        <span>{item.rawPayment.moloniDocumentId || item.rawPayment.hasInvoice ? "Ver Fatura" : "Fatura"}</span>
                                                    </button>
                                                )}
                                                {isCompleted && isAdmin && !actionLoading && (item.rawPayment || (isInscriptionItem && item.athleteTeamId)) && (
                                                    <button
                                                        className="phs-delete-btn"
                                                        onClick={() => {
                                                            if (item.rawPayment) {
                                                                handleDeletePayment(item.rawPayment.id, item.title);
                                                            } else if (isInscriptionItem && item.athleteTeamId) {
                                                                handleDeleteInscription(item.athleteTeamId, item.title);
                                                            }
                                                        }}
                                                        title="Eliminar registo"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentHistorySocio;